import {
  Box,
  Card,
  Divider,
  Grid,
  Tooltip,
  Typography,
  alpha,
  useTheme
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import PaymentsOutlined from '@mui/icons-material/PaymentsOutlined';
import ReceiptLongOutlined from '@mui/icons-material/ReceiptLongOutlined';
import CardGiftcardOutlined from '@mui/icons-material/CardGiftcardOutlined';
import AccountBalanceWalletOutlined from '@mui/icons-material/AccountBalanceWalletOutlined';
import ScheduleOutlined from '@mui/icons-material/ScheduleOutlined';
import TrendingUp from '@mui/icons-material/TrendingUp';
import InsightsOutlined from '@mui/icons-material/InsightsOutlined';
import StarOutline from '@mui/icons-material/StarOutline';
import LocalOfferOutlined from '@mui/icons-material/LocalOfferOutlined';
import StarsOutlined from '@mui/icons-material/StarsOutlined';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import { SalesSummary } from '@/types/SalesSummary.interface';
import {
  EntryMode,
  VOUCHER_REWARD_TYPES,
  averageCard,
  formatCount,
  formatHourRange,
  formatMoney,
  formatPercent,
  formatReportDate,
  freeOrderRows,
  loyaltySourceRows,
  paymentBucketRows,
  percentOf,
  receivableRows,
  ReportPreset,
  salesDetailRows
} from '@/utils/salesReport';
import {
  compareLabelKey,
  dayCount,
  MetricComparison,
  PeriodComparison
} from '@/utils/reportCompare';
import {
  DeltaBadge,
  ReportCard,
  ReportEmpty,
  ReportLineRow,
  ReportStatPair,
  ReportTotalRow
} from './ReportParts';
import { HourlySalesChart, SalesTrendBars } from './ReportCharts';

interface SectionProps {
  summary: SalesSummary;
}

/** Toplam Satış + Ortalama Sepet (tanım notuyla). */
interface TotalsCardsProps extends SectionProps {
  entryMode: EntryMode;
  /** Önceki döneme göre kıyas; yoksa (yükleniyor / hata) rozet çizilmez. */
  comparison?: PeriodComparison | null;
  preset?: ReportPreset;
}

export const TotalsCards = ({
  summary,
  entryMode,
  comparison,
  preset = 'custom'
}: TotalsCardsProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const average = averageCard(summary, entryMode);

  const compareLabel = comparison
    ? t(compareLabelKey(preset), { n: dayCount(comparison.previous) })
    : '';
  const windowLine = comparison
    ? comparison.previous.startDate === comparison.previous.endDate
      ? t('salesReport.compare.previousDay', {
          date: formatReportDate(comparison.previous.startDate)
        })
      : t('salesReport.compare.previousWindow', {
          start: formatReportDate(comparison.previous.startDate),
          end: formatReportDate(comparison.previous.endDate)
        })
    : '';
  const partialHour = comparison?.partialThroughHour ?? null;
  const details = (metric: MetricComparison, extra?: string) =>
    [
      windowLine,
      t('salesReport.compare.previousValue', {
        value: formatMoney(metric.previousValue)
      }),
      ...(extra ? [extra] : [])
    ].filter(Boolean);

  const cards = [
    {
      key: 'total',
      title: t('salesReport.totalSales'),
      value: formatMoney(summary.totals.totalSales),
      sub: t('salesReport.transactions', {
        n: formatCount(summary.totals.transactionCount)
      }),
      note: t('salesReport.totalSales.hint'),
      color: theme.palette.primary.main,
      delta: comparison
        ? {
            change: comparison.totalSales.change,
            details: details(
              comparison.totalSales,
              partialHour === null
                ? undefined
                : t('salesReport.compare.partialHint', {
                    until: `${String((partialHour + 1) % 24).padStart(2, '0')}:00`
                  })
            )
          }
        : null
    },
    {
      key: 'basket',
      title: t(average.titleKey),
      value: formatMoney(average.value),
      sub: t(average.subKey, { n: formatCount(average.count) }),
      note: t(average.hintKey),
      color: theme.palette.success.main,
      delta: comparison
        ? {
            change: comparison.average.change,
            details: details(
              comparison.average,
              partialHour === null
                ? undefined
                : t('salesReport.compare.averageFullDay')
            )
          }
        : null
    }
  ];

  return (
    <Grid container spacing={3}>
      {cards.map((card) => (
        <Grid item xs={12} md={6} key={card.key}>
          <Card
            sx={{
              p: 2.5,
              height: '100%',
              boxShadow: theme.shadows[2],
              background: `linear-gradient(135deg, ${alpha(card.color, 0.08)} 0%, ${alpha(card.color, 0.18)} 100%)`
            }}
          >
            <Box display="flex" alignItems="center" gap={1}>
              <Typography
                variant="overline"
                color="text.secondary"
                fontWeight="bold"
                sx={{ flex: 1 }}
              >
                {card.title}
              </Typography>
              <Tooltip title={card.note} arrow>
                <InfoOutlined fontSize="small" color="action" />
              </Tooltip>
            </Box>
            <Typography
              variant="h2"
              fontWeight="bold"
              sx={{ color: card.color, my: 0.5 }}
            >
              {card.value}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {card.sub}
            </Typography>
            {card.delta && (
              <DeltaBadge
                change={card.delta.change}
                label={compareLabel}
                details={card.delta.details}
              />
            )}
            <Typography
              variant="caption"
              sx={{ textTransform: 'none' }}
              color="text.secondary"
              display="block"
              mt={1}
            >
              {card.note}
            </Typography>
          </Card>
        </Grid>
      ))}
    </Grid>
  );
};

export const PaymentTypesCard = ({ summary }: SectionProps) => {
  const { t } = useTranslation();
  const total = summary.totals.totalSales;
  return (
    <ReportCard
      title={t('salesReport.payments')}
      icon={<PaymentsOutlined color="primary" />}
      footnote={t('salesReport.payments.hint')}
    >
      {paymentBucketRows(summary).map((line) => (
        <ReportLineRow
          key={line.key}
          line={line}
          whole={total}
          countLabelKey="salesReport.transactions"
        />
      ))}
      <ReportTotalRow
        label={t('salesReport.totalSales')}
        count={summary.totals.transactionCount}
        total={total}
        countLabelKey="salesReport.transactions"
      />
    </ReportCard>
  );
};

export const SalesDetailsCard = ({ summary }: SectionProps) => {
  const { t } = useTranslation();
  return (
    <ReportCard
      title={t('salesReport.details')}
      icon={<ReceiptLongOutlined color="primary" />}
    >
      {salesDetailRows(summary).map((line) => (
        <ReportLineRow key={line.key} line={line} />
      ))}
      <ReportTotalRow
        label={t('salesReport.totalSales')}
        count={summary.totals.transactionCount}
        total={summary.totals.totalSales}
        countLabelKey="salesReport.transactions"
      />
    </ReportCard>
  );
};

export const FreeOrdersCard = ({ summary }: SectionProps) => {
  const { t } = useTranslation();
  const rows = freeOrderRows(summary);
  return (
    <ReportCard
      title={t('salesReport.free')}
      icon={<CardGiftcardOutlined color="secondary" />}
      footnote={t('salesReport.free.hint')}
    >
      <ReportStatPair
        items={[
          {
            label: t('salesReport.free.count'),
            value: formatCount(summary.freeOrders.count)
          },
          {
            label: t('salesReport.free.listValue'),
            value: formatMoney(summary.freeOrders.listValue)
          }
        ]}
      />
      {rows.length === 0 ? (
        <ReportEmpty />
      ) : (
        rows.map((line) => <ReportLineRow key={line.key} line={line} />)
      )}
    </ReportCard>
  );
};

export const ReceivablesCard = ({ summary }: SectionProps) => {
  const { t } = useTranslation();
  return (
    <ReportCard
      title={t('salesReport.receivables')}
      icon={<AccountBalanceWalletOutlined sx={{ color: '#5D4037' }} />}
      footnote={t('salesReport.receivables.hint')}
    >
      {receivableRows(summary).map((line) => (
        <ReportLineRow key={line.key} line={line} />
      ))}
      <ReportTotalRow
        label={t('salesReport.total')}
        total={summary.receivableCollections.total}
      />
    </ReportCard>
  );
};

export const HourlyCard = ({ summary }: SectionProps) => {
  const { t } = useTranslation();
  const hasData = summary.hourly.some((h) => h.transactions > 0);
  return (
    <ReportCard
      title={t('salesReport.hourly')}
      icon={<ScheduleOutlined color="warning" />}
    >
      {hasData ? (
        <HourlySalesChart
          hourly={summary.hourly}
          seriesName={t('salesReport.totalSales')}
        />
      ) : (
        <ReportEmpty />
      )}
    </ReportCard>
  );
};

export const PeakHoursCard = ({ summary }: SectionProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const peaks = summary.peakHours.filter((p) => p.transactions > 0);
  const max = peaks[0]?.sales ?? 0;
  return (
    <ReportCard
      title={t('salesReport.peakHours')}
      icon={<TrendingUp color="success" />}
    >
      {peaks.length === 0 ? (
        <ReportEmpty />
      ) : (
        peaks.map((peak, index) => (
          <Box key={peak.hour} py={0.75}>
            <Box display="flex" alignItems="baseline" gap={1}>
              <Typography variant="body2" fontWeight={700} sx={{ width: 28 }}>
                #{index + 1}
              </Typography>
              <Typography
                variant="body1"
                fontWeight={600}
                sx={{ flex: 1 }}
                noWrap
              >
                {formatHourRange(peak.hour)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('salesReport.transactions', {
                  n: formatCount(peak.transactions)
                })}
              </Typography>
              <Typography
                variant="body1"
                fontWeight={700}
                sx={{ minWidth: 110, textAlign: 'right' }}
              >
                {formatMoney(peak.sales)}
              </Typography>
            </Box>
            <Box
              sx={{
                mt: 0.5,
                ml: 4.5,
                height: 6,
                borderRadius: 1,
                background: alpha(theme.palette.success.main, 0.18)
              }}
            >
              <Box
                sx={{
                  width: `${percentOf(peak.sales, max)}%`,
                  height: '100%',
                  borderRadius: 1,
                  background: theme.palette.success.main
                }}
              />
            </Box>
          </Box>
        ))
      )}
    </ReportCard>
  );
};

export const TrendCard = ({ summary }: SectionProps) => {
  const { t } = useTranslation();
  const title =
    summary.window.trendInterval === 'month'
      ? t('salesReport.trend.month')
      : t('salesReport.trend.day');
  return (
    <ReportCard title={title} icon={<InsightsOutlined color="info" />}>
      {summary.trend.length === 0 ? (
        <ReportEmpty />
      ) : (
        <SalesTrendBars
          trend={summary.trend}
          seriesName={t('salesReport.totalSales')}
        />
      )}
    </ReportCard>
  );
};

export const TopProductsCard = ({ summary }: SectionProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  return (
    <ReportCard
      title={t('salesReport.topProducts')}
      icon={<StarOutline sx={{ color: '#FF9800' }} />}
      footnote={t('salesReport.topProducts.hint')}
    >
      {summary.topProducts.length === 0 ? (
        <ReportEmpty />
      ) : (
        summary.topProducts.map((product, index) => (
          <Box
            key={product.id}
            display="flex"
            alignItems="center"
            gap={1.5}
            py={0.75}
          >
            <Box
              sx={{
                width: 26,
                height: 26,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 12,
                fontWeight: 700,
                color: theme.palette.common.white,
                backgroundColor:
                  index < 3 ? '#FF9800' : theme.palette.grey[500],
                flexShrink: 0
              }}
            >
              {index + 1}
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="body1" fontWeight={600} noWrap>
                {product.name}
              </Typography>
              <Typography
                variant="caption"
                sx={{ textTransform: 'none' }}
                color="text.secondary"
              >
                {t('salesReport.count', {
                  n: formatCount(product.quantity)
                })}
                {product.freeQuantity > 0 &&
                  ` · ${t('salesReport.topProducts.free', {
                    n: formatCount(product.freeQuantity)
                  })}`}
              </Typography>
            </Box>
            <Typography variant="body1" fontWeight={700}>
              {formatMoney(product.revenue)}
            </Typography>
          </Box>
        ))
      )}
    </ReportCard>
  );
};

export const VouchersCard = ({ summary }: SectionProps) => {
  const { t } = useTranslation();
  const v = summary.vouchers;
  return (
    <ReportCard
      title={t('salesReport.vouchers')}
      icon={<LocalOfferOutlined sx={{ color: '#FF5722' }} />}
    >
      {v.totalUsages === 0 ? (
        <ReportEmpty />
      ) : (
        <>
          <ReportStatPair
            items={[
              {
                label: t('salesReport.vouchers.usages'),
                value: formatCount(v.totalUsages)
              },
              {
                label: t('salesReport.vouchers.discount'),
                value: formatMoney(v.totalDiscountAmount),
                color: '#F44336'
              }
            ]}
          />
          {VOUCHER_REWARD_TYPES.filter(
            (type) => v.byRewardType[type].count > 0
          ).map((type) => (
            <Box
              key={type}
              display="flex"
              alignItems="baseline"
              gap={1}
              py={0.5}
            >
              <Typography variant="body1" sx={{ flex: 1 }}>
                {t(`salesReport.vouchers.${type}`)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('salesReport.vouchers.uses', {
                  n: formatCount(v.byRewardType[type].count)
                })}
              </Typography>
              <Typography
                variant="body1"
                fontWeight={700}
                sx={{ minWidth: 110, textAlign: 'right' }}
              >
                {formatMoney(v.byRewardType[type].totalDiscount)}
              </Typography>
            </Box>
          ))}
          {v.topVouchers.length > 0 && (
            <>
              <Divider sx={{ my: 1.5 }} />
              <Typography variant="subtitle2" color="text.secondary" mb={1}>
                {t('salesReport.vouchers.top')}
              </Typography>
              {v.topVouchers.map((voucher) => (
                <Box
                  key={voucher.templateId}
                  display="flex"
                  alignItems="baseline"
                  gap={1}
                  py={0.5}
                >
                  <Typography
                    variant="body2"
                    sx={{ flex: 1, minWidth: 0 }}
                    noWrap
                  >
                    {voucher.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('salesReport.vouchers.uses', {
                      n: formatCount(voucher.usageCount)
                    })}
                  </Typography>
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    sx={{ minWidth: 110, textAlign: 'right' }}
                  >
                    {formatMoney(voucher.totalDiscount)}
                  </Typography>
                </Box>
              ))}
            </>
          )}
        </>
      )}
    </ReportCard>
  );
};

export const LoyaltyCard = ({ summary }: SectionProps) => {
  const { t } = useTranslation();
  const l = summary.loyalty;
  const sources = loyaltySourceRows(summary);
  return (
    <ReportCard
      title={t('salesReport.loyalty')}
      icon={<StarsOutlined sx={{ color: '#9C27B0' }} />}
    >
      {l.totalEarned === 0 && l.totalRedeemed === 0 ? (
        <ReportEmpty />
      ) : (
        <>
          <ReportStatPair
            items={[
              {
                label: t('salesReport.loyalty.earned'),
                value: `+${formatCount(l.totalEarned)}`,
                color: '#4CAF50'
              },
              {
                label: t('salesReport.loyalty.redeemed'),
                value: `-${formatCount(l.totalRedeemed)}`,
                color: '#F44336'
              }
            ]}
          />
          {sources.length > 0 && (
            <>
              <Typography variant="subtitle2" color="text.secondary" mb={0.5}>
                {t('salesReport.loyalty.sources')}
              </Typography>
              {sources.map((source) => (
                <Box
                  key={source.key}
                  display="flex"
                  alignItems="baseline"
                  gap={1}
                  py={0.5}
                >
                  <Typography variant="body1" sx={{ flex: 1 }}>
                    {t(source.labelKey, { defaultValue: source.key })}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {formatPercent(source.points, l.totalEarned)}
                  </Typography>
                  <Typography
                    variant="body1"
                    fontWeight={700}
                    sx={{ minWidth: 110, textAlign: 'right' }}
                  >
                    +
                    {t('salesReport.points', {
                      n: formatCount(source.points)
                    })}
                  </Typography>
                </Box>
              ))}
            </>
          )}
          <Divider sx={{ my: 1.5 }} />
          <Box display="flex" alignItems="baseline" gap={1}>
            <Typography variant="body1" sx={{ flex: 1 }}>
              {t('salesReport.loyalty.redemptions')}
            </Typography>
            <Typography variant="body1" fontWeight={700}>
              {t('salesReport.loyalty.redemptions.value', {
                n: formatCount(l.redemptions.count),
                points: formatCount(l.redemptions.totalPointsSpent)
              })}
            </Typography>
          </Box>
        </>
      )}
    </ReportCard>
  );
};
